/**
 * `/oauth/start` - courtesy landing page after an operator-initiated
 * API-key reissue (OCP-156).
 *
 * `ApiKeysPage.reissue()` returns a `reauthorize_url` that points HERE
 * with query params (client_id, reason, scope[], connection_id). The
 * customer's browser hits `account.orboto.io/oauth/start?...` after
 * `window.location.assign` - but the actual PKCE-bearing OAuth round-
 * trip has to start from the product (it owns the verifier). So this
 * page is a landing card, not an initiator.
 *
 * Was missing - backend baked the URL into `reauthorize_url` but the
 * SPA had no route -> fell through to `<Navigate to="/" />`. Same shape
 * as OCP-145 (password-reset-confirm) and OCP-155 (verify-email).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { useAuth } from '../lib/auth-context';
import { LegalFooter } from '../components/LegalFooter';

export function OAuthStartPage() {
  const { t } = useTranslation('oauth-start');
  const [params] = useSearchParams();
  const location = useLocation();
  const { status } = useAuth();

  const clientId = params.get('client_id');
  const reason = params.get('reason') ?? 'connect';
  const connectionId = params.get('connection_id');
  const scopes = useMemo(() => params.getAll('scope'), [params]);

  // Auth-gate: if the customer isn't signed in, bounce to /login with a
  // from=... so they come right back after auth.
  if (status === 'unauthenticated') {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?from=${encodeURIComponent(from)}`} replace />;
  }
  // Wait until auth is resolved before deciding what to render.
  if (status === 'loading') {
    return (
      <div className="px-6 py-10 text-sm text-gray-500">{t('checkingSession')}</div>
    );
  }

  const variant = reason === 'reissue' ? 'reissue' : 'connect';

  return (
    <div className="mx-auto max-w-xl px-6 py-10">
      <h1 className="text-2xl font-semibold tracking-tight">{t(`headline.${variant}`)}</h1>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {t(`intro.${variant}`)}
      </p>

      <section className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm shadow-sm">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('details')}
        </h2>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-3">
          {clientId && (
            <>
              <dt className="text-xs text-gray-500">{t('client')}</dt>
              <dd className="col-span-2 font-mono text-xs">{clientId}</dd>
            </>
          )}
          {connectionId && (
            <>
              <dt className="text-xs text-gray-500">{t('previousConnection')}</dt>
              <dd className="col-span-2 font-mono text-xs">{connectionId}</dd>
            </>
          )}
          {scopes.length > 0 && (
            <>
              <dt className="text-xs text-gray-500">{t('scopesRequested')}</dt>
              <dd className="col-span-2">
                <ul className="list-disc pl-5 text-xs text-gray-700 dark:text-gray-300">
                  {scopes.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </dd>
            </>
          )}
        </dl>
      </section>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/mail/connections"
          className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700"
        >
          {t('viewConnections')}
        </Link>
        <Link
          to="/"
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('backToOverview')}
        </Link>
      </div>
      <LegalFooter className="mt-8" />
    </div>
  );
}
